import { SpreadsheetType } from "@modules/spreadsheets/dto/spreadsheet/type";
import {
  SpreadsheetTableQueryModel,
  SpreadsheetTableWhereClause,
  SPREADSHEET_TABLE_CONSTANTS,
} from "./table.types";
import { OPERATOR_MAPPING, SPREADSHEET_FIELD_CONTEXT } from "@modules/spreadsheets/const/common";
import { SkipLogicConditionAndOr, SkipLogicConditionOperator } from "@common/components/skip-logic/skip-logic.types";

export type ComputeQueryModelOptions = {
  apiQuery?: { whereClauses: any[]; andOR?: number };
  runPayloadQuery?: { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number };
  userFilter?: { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number };
  normalizeForSave?: boolean;
};

export const VALUE_OPTIONAL_OPERATOR_STRINGS = new Set<string>([
  SkipLogicConditionOperator.isBlank,
  SkipLogicConditionOperator.isNotBlank,
  "IS_NULL",
  "IS_NOT_NULL",
]);

export const VALUE_OPTIONAL_OPERATOR_NUMBERS = new Set<number>([
  OPERATOR_MAPPING?.IS_BLANK,
  OPERATOR_MAPPING?.IS_NOT_BLANK,
  OPERATOR_MAPPING?.IS_NULL,
  OPERATOR_MAPPING?.IS_NOT_NULL,
].filter((value): value is number => typeof value === "number"));

export const isValueOptionalOperator = (operator: unknown): boolean => {
  if (operator === null || operator === undefined) {
    return false;
  }

  if (typeof operator === "string") {
    return VALUE_OPTIONAL_OPERATOR_STRINGS.has(operator.toUpperCase());
  }

  if (typeof operator === "number") {
    return VALUE_OPTIONAL_OPERATOR_NUMBERS.has(operator);
  }

  return false;
};

export const MISSING_VALUE_ERROR_MESSAGE =
  "The spreadsheet could not be run because a comparison operator is missing a value for the following field(s) in the query. Please fix this error and re-run the spreadsheet:";

export const hasConditionValue = (conditionValue: any): boolean => {
  if (conditionValue === null || conditionValue === undefined) {
    return false;
  }

  if (Array.isArray(conditionValue)) {
    return conditionValue.length > 0;
  }

  if (typeof conditionValue === "object") {
    const objectValue =
      conditionValue.dataValue ??
      conditionValue.rawValue ??
      conditionValue.updateValue ??
      conditionValue.value ??
      conditionValue.displayValue;

    return (
      objectValue !== null &&
      objectValue !== undefined &&
      String(objectValue).trim() !== ""
    );
  }

  if (typeof conditionValue === "string") {
    return conditionValue.trim() !== "";
  }

  return String(conditionValue).trim() !== "";
};

// Internal: transform API format clause to UI/backend-expected clause
const transformApiQueryClause = (
  apiClause: any,
  spreadsheetType: string
): SpreadsheetTableWhereClause => {
  const fieldId = apiClause.fieldId || apiClause.field?.fieldid;
  const operator = apiClause.operator;
  const andOr = apiClause.andOR;

  let value = "";
  if (apiClause.value) {
    if (typeof apiClause.value === "string") value = apiClause.value;
    else if (apiClause.value.dataValue !== undefined) value = apiClause.value.dataValue;
    else if (apiClause.value.rawValue !== undefined) value = apiClause.value.rawValue;
    else if (apiClause.value.updateValue !== undefined) value = apiClause.value.updateValue;
  }

  const field = apiClause.field || {};
  const type = field?.fieldtype?.typeid || (field?.system ? -100 : field?.fieldtype?.typeid) || 5;
  const system = field?.system || false;
  const fieldClass = field?.fieldClass || "java.lang.String";

  const result: SpreadsheetTableWhereClause = {
    field: {
      fieldid: fieldId || 0,
      type,
      fieldcontext:
        String(spreadsheetType) === String(SpreadsheetType.individual)
          ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
          : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
      system,
    },
    operator,
    value,
    fieldClass,
    leftParenthesis: apiClause.leftParenthesis || false,
    rightParenthesis: apiClause.rightParenthesis || false,
  } as SpreadsheetTableWhereClause;

  if (apiClause.parentField) {
    const parentField = apiClause.parentField;
    (result as any).parentField = {
      fieldid: parentField.fieldid || parentField.fieldId || 0,
      type: parentField.fieldtype?.typeid || parentField.type || 5,
      fieldcontext:
        String(spreadsheetType) === String(SpreadsheetType.individual)
          ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL
          : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
      system: parentField.system || false,
    };
  }
  if (andOr !== undefined && andOr !== null) (result as any).andOr = andOr.toString();
  return result;
};

// Internal: merge api/run/user filters into one query model
const buildUnifiedQueryModel = (
  spreadsheetType: string,
  opts: {
    apiQuery?: { whereClauses: any[]; andOR?: number };
    runPayloadQuery?: { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number };
    userFilter?: { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number };
  }
): SpreadsheetTableQueryModel => {
  const { apiQuery, runPayloadQuery, userFilter } = opts;

  // Gather base clauses from both apiQuery and runPayloadQuery
  const apiClauses = (apiQuery?.whereClauses || []).map((c: any) =>
    transformApiQueryClause(c, spreadsheetType)
  ) as SpreadsheetTableWhereClause[];
  const runClauses = (runPayloadQuery?.whereClauses || []) as SpreadsheetTableWhereClause[];
  const baseClauses = [...apiClauses, ...runClauses];

  if (!userFilter) {
    return { whereClauses: baseClauses, ...(apiQuery?.andOR !== undefined && { andOR: apiQuery.andOR }) } as SpreadsheetTableQueryModel;
  }

  // Merge base with user filter
  const userClauses = userFilter?.whereClauses || [];
  const mergedClauses = [...baseClauses, ...userClauses];
  let andOR = (userFilter as any)?.andOR ?? apiQuery?.andOR ?? runPayloadQuery?.andOR;
  if (andOR === undefined && mergedClauses.length > 0) {
    const firstAndOr = (mergedClauses[0] as any)?.andOr;
    if (firstAndOr === "AND") andOR = 0;
    else if (firstAndOr === "OR") andOR = 1;
  }
  const merged: SpreadsheetTableQueryModel = {
    whereClauses: mergedClauses,
    ...(andOR !== undefined && { andOR }),
  };

  if (merged.whereClauses.length > 0 && (merged as any).andOR === undefined) {
    (merged as any).andOR = 0;
  }

  return merged;
};

// Internal: normalize by moving top-level andOR into first clause and deleting it
const normalizeQueryToClause = (
  query: SpreadsheetTableQueryModel
): SpreadsheetTableQueryModel => {
  if (!query) return { whereClauses: [] } as SpreadsheetTableQueryModel;
  const { whereClauses = [] } = query as any;
  const andOR = (query as any).andOR ?? (query as any).andOr;

  if (whereClauses.length === 0) {
    return { whereClauses: [] } as SpreadsheetTableQueryModel;
  }

  // If there's no top-level andOR, return as-is (already normalized)
  if (andOR === undefined) {
    return query;
  }

  const first = { ...(whereClauses[0] || {}) } as any;
  const rest = whereClauses.slice(1);

  // Only set first.andOr if it's not already set
  if (first.andOr === undefined || first.andOr === null) {
    first.andOr = andOR === 0 ? "AND" : andOR === 1 ? "OR" : String(andOR);
  }


  return {
    whereClauses: [first, ...rest],
  } as SpreadsheetTableQueryModel;
};

/**
 * Compute a query model for any spreadsheet scenario by combining API query,
 * run payload query, and current user filters. Optionally normalizes for save API.
 */
export const computeQueryModel = (
  spreadsheetType: SpreadsheetType | string,
  opts: ComputeQueryModelOptions
): SpreadsheetTableQueryModel => {
  const unified = buildUnifiedQueryModel(String(spreadsheetType), {
    apiQuery: opts.apiQuery,
    runPayloadQuery: opts.runPayloadQuery,
    userFilter: opts.userFilter,
  });
  const normalized = normalizeQueryToClause(unified);
  return normalized;
};



/**
 * Validates query conditions (AND/OR and parentheses)
 * 
 * @param conditions - Array of query conditions to validate
 * @returns Object with isValid flag and optional errorMessage
 */
export const validateQueryConditions = (
  conditions: any[]
): { isValid: boolean; errorMessage?: string } => {
  // If no conditions, validation passes
  if (!conditions || conditions.length === 0) {
    return { isValid: true };
  }

  const whereSize = conditions.length;
  let openedParenthesis = 0;
  let closedParenthesis = 0;

  // Check each condition
  for (let i = 0; i < whereSize; i++) {
    const condition = conditions[i];
    if (!condition) {
      continue; // Skip null/undefined conditions
    }
    
    // Check both andOR and andOr (case variations) - prioritize andOR
    // Also handle enum values (SkipLogicConditionAndOr.and = "AND", SkipLogicConditionAndOr.or = "OR")
    const andOr = condition.andOR !== undefined && condition.andOR !== null 
      ? condition.andOR 
      : condition.andOr !== undefined && condition.andOr !== null
        ? condition.andOr
        : undefined;

    // Check if AND/OR is missing for non-last rows (skip this check if only one condition)
    // This matches GWT logic: if (i < li_whereSize - 1 && (ls_andOr == null || ls_andOr.isEmpty()))
    // Valid values are "AND", "OR", or the enum values SkipLogicConditionAndOr.and/or
    if (whereSize > 1 && i < whereSize - 1) {
      // Check if andOr is a valid value (string "AND"/"OR" or enum)
      const isValidAndOr = (andOr === "AND" || andOr === "OR" || 
                            andOr === SkipLogicConditionAndOr?.and || 
                            andOr === SkipLogicConditionAndOr?.or) &&
                            andOr !== "" && 
                            andOr !== null && 
                            andOr !== undefined;
      
      if (!isValidAndOr) {
        return {
          isValid: false,
          errorMessage:"The query is not formed properly, please verify that the AND/OR column is entered correctly for each row."
        };
      }
    }

    // Count parentheses
    if (condition.leftParenthesis) {
      openedParenthesis++;
    }
    if (condition.rightParenthesis) {
      closedParenthesis++;
    }
  }

  // Check if parentheses are balanced
  // This matches GWT logic: if (li_openedParenthesis != li_closedParenthesis)
  if (openedParenthesis !== closedParenthesis) {
    return {
      isValid: false,
      errorMessage: "Incorrect use of parentheses."
    };
  }

  return { isValid: true };
};